import { Lightbulb, Sparkles, Target, TrendingUp, Activity, Gauge } from 'lucide-react';
import { useMemo } from 'react';
import AvatarIndicator from './AvatarIndicator';
import type { VisualIndicator } from '../types/diagnostics';

interface TalentDiagnosticsSectionResult {
  name?: string;
  score?: number;
  weighting?: number;
  isSkipped?: boolean;
  answeredQuestionCount?: number;
  skippedQuestionCount?: number;
}

interface TalentDiagnosticsRecommendationItem {
  title?: string;
  overview?: string;
  recommendedActions?: string[];
  expectedOutcome?: string;
}

interface TalentDiagnosticsSummaryProps {
  overallPercentage: number;
  visualIndicator: VisualIndicator;
  benchmarkName?: string;
  benchmarkDescription?: string;
  benchmarkRecommendationContent?: string;
  sectionResults?: TalentDiagnosticsSectionResult[];
  strengths?: string[];
  improvementOpportunities?: string[];
  recommendations?: TalentDiagnosticsRecommendationItem[];
  diagnosticStats?: Record<string, string | number | undefined>;
  report?: string[];
}

export default function TalentDiagnosticsSummary({
  overallPercentage,
  visualIndicator,
  benchmarkName,
  benchmarkDescription,
  benchmarkRecommendationContent,
  sectionResults = [],
  strengths = [],
  improvementOpportunities = [],
  recommendations = [],
  diagnosticStats = {},
  report = [],
}: TalentDiagnosticsSummaryProps) {
  const normalizedRecommendations = useMemo(
    () =>
        recommendations.filter(
        (item) =>
            item.title ||
            item.overview ||
            item.expectedOutcome
        ),
        [recommendations]
    );
  
  const displayedStrengths = useMemo(
    () =>
        strengths.length > 0
        ? strengths
        : sectionResults
            .filter(
                (section) =>
                !section.isSkipped &&
                (section.score ?? 0) >= 70
            )
            .map(
                (section) => section.name || ''
            ),
    [strengths, sectionResults]
  );

  const executiveSummary = useMemo(() => {
  const strength =
    displayedStrengths[0] || 'Talent Acquisition';

  const recommendation =
    normalizedRecommendations[0];

  return [
    `Your Talent Acquisition function achieved an overall score of ${overallPercentage}%, placing the organisation in the ${benchmarkName || 'assessed'} maturity category.`,

    `${strength} emerged as a key area of strength.`,

    recommendation?.overview,

    recommendation?.expectedOutcome
      ? `Expected outcome: ${recommendation.expectedOutcome}`
      : undefined,
  ]
    .filter(Boolean)
    .join(' ');
}, [
  overallPercentage,
  benchmarkName,
  displayedStrengths,
  normalizedRecommendations,
]);

  // const displayedImprovements = useMemo(
  //   () =>
  //       improvementOpportunities.length > 0
  //       ? improvementOpportunities
  //       : sectionResults
  //           .filter(
  //               (section) =>
  //               !section.isSkipped &&
  //               (section.score ?? 0) < 70
  //           )
  //           .map(
  //               (section) => section.name || ''
  //           ),
  //   [improvementOpportunities, sectionResults]
  // );

  const handleBookingClick = () => {
    window.open('https://app.strivreservices.com/book/strivre-services', "_blank", "noopener,noreferrer");
  };

  return (
    <div className="space-y-8 animate-fade-in mb-5 w-full">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-stretch">
        <div className="bg-white rounded-3xl border border-slate-200 shadow-sm p-8 flex flex-col items-center justify-center h-full relative overflow-hidden group">
          <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-violet-500 via-sky-500 to-cyan-500"></div>

          <div className="flex-shrink-0">
            <AvatarIndicator state={visualIndicator} />
          </div>

          <div className="flex flex-col items-center text-center">
            <div className="bg-slate-50/50 backdrop-blur-sm px-10 py-6 rounded-[2.5rem] border border-slate-100 shadow-inner transition-all group-hover:bg-white group-hover:shadow-md">
              <span className="text-7xl font-black text-slate-900 tracking-tighter leading-none block">
                {overallPercentage}
                <span className="text-3xl text-violet-600 font-bold ml-1">%</span>
              </span>
              <span className="text-[11px] font-bold text-slate-400 uppercase tracking-[0.2em] mt-3 block">
                Talent Acquisition Health
              </span>
              {benchmarkName && (
                <div className="mt-4">
                    <span className="px-4 py-2 rounded-full bg-violet-100 text-violet-700 text-sm font-semibold">
                    {benchmarkName}
                    </span>
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="bg-gradient-to-br from-violet-600 to-indigo-700 rounded-3xl p-10 text-white shadow-xl h-full">
            <div className="flex items-center gap-3 mb-6">
                <Gauge size={24} />
                <h3 className="text-2xl font-bold">Executive Summary</h3>
            </div>

            <div className="grid md:grid-cols-2 gap-4 mb-8">
                <div className="bg-white/10 rounded-2xl p-5">
                <div className="text-xs uppercase tracking-wide opacity-80">
                    Maturity Level
                </div>

                <div className="text-lg font-bold mt-2 leading-tight break-words">
                    {benchmarkName || 'N/A'}
                </div>
                </div>

                <div className="bg-white/10 rounded-2xl p-5">
                <div className="text-xs uppercase tracking-wide opacity-80">
                    Top Strength
                </div>

                <div className="text-lg font-bold mt-2 leading-tight break-words">
                    {displayedStrengths[0] || 'N/A'}
                </div>
                </div>
            </div>

            <p className="text-lg leading-relaxed text-violet-50">
                {executiveSummary}
            </p>
        </div>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 items-stretch">
        {/* Maturity Benchmark */}
        <div className="bg-white rounded-3xl border border-slate-100 shadow-sm p-6 hover:border-violet-200 transition-all h-full">
            <div className="flex items-start justify-between mb-5">
            <div className="p-3 rounded-2xl bg-violet-50 border border-violet-100 text-violet-600">
                <TrendingUp size={24} />
            </div>

            <span className="inline-flex items-center px-4 py-2 rounded-full bg-violet-100 text-violet-700 text-sm font-bold">
                {benchmarkName || 'Benchmark'}
            </span>
            </div>

            <div>
            <h4 className="text-sm font-bold text-slate-800 uppercase tracking-[0.15em] mb-4">
                Maturity Benchmark
            </h4>

            <div className="bg-slate-50 rounded-2xl p-4 border border-slate-100">
                <p className="text-sm text-slate-600 leading-relaxed">
                {benchmarkDescription ||
                    'Benchmark details will appear here once the assessment is analysed.'}
                </p>
            </div>

            <div className="mt-6">
                <p className="text-xs font-semibold uppercase tracking-[0.15em] text-violet-600 mb-3">
                Focus Areas
                </p>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {benchmarkRecommendationContent
                    ?.split(',')
                    .map((item) => (
                    <div
                        key={item.trim()}
                        className="flex items-center gap-2 px-3 py-2 rounded-xl bg-violet-50 border border-violet-100"
                    >
                        <Target
                        size={14}
                        className="text-violet-600 flex-shrink-0"
                        />

                        <span className="text-sm font-medium text-violet-700">
                        {item.trim()}
                        </span>
                    </div>
                    ))}
                </div>
            </div>
            </div>
        </div>
        {/* Diagnostic Statistics */}
        <div className="bg-white rounded-3xl border border-slate-100 shadow-sm p-6 hover:border-sky-200 transition-all h-full">
            <div className="flex items-start justify-between mb-5">
                <div className="p-3 rounded-2xl bg-sky-50 border border-sky-100 text-sky-600">
                    <Gauge size={24} />
                </div>

                <span className="text-3xl font-black tracking-tighter text-sky-600">
                    {Object.keys(diagnosticStats).length ? 'Live' : 'Ready'}
                </span>
            </div>

            <h4 className="text-sm font-bold text-slate-800 uppercase tracking-[0.15em] mb-4">
            Diagnostic Statistics
            </h4>

            <div className="grid grid-cols-2 gap-3">

            <div className="bg-slate-50 rounded-2xl p-4 border border-slate-100">
                <p className="text-xs uppercase tracking-wide text-slate-500">
                Answered Questions
                </p>
                <p className="text-2xl font-black text-slate-900 mt-1">
                {diagnosticStats.AnsweredQuestionCount ?? '-'}
                </p>
            </div>

            <div className="bg-slate-50 rounded-2xl p-4 border border-slate-100">
                <p className="text-xs uppercase tracking-wide text-slate-500">
                Skipped Questions
                </p>
                <p className="text-2xl font-black text-slate-900 mt-1">
                {diagnosticStats.SkippedQuestionCount ?? '-'}
                </p>
            </div>

            <div className="bg-slate-50 rounded-2xl p-4 border border-slate-100">
                <p className="text-xs uppercase tracking-wide text-slate-500">
                Weighting Used
                </p>
                <p className="text-2xl font-black text-slate-900 mt-1">
                {diagnosticStats.EffectiveWeightingUsed ?? '-'}
                </p>
            </div>

            <div className="bg-slate-50 rounded-2xl p-4 border border-slate-100">
                <p className="text-xs uppercase tracking-wide text-slate-500">
                Answered Sections
                </p>
                <p className="text-2xl font-black text-slate-900 mt-1">
                {diagnosticStats.AnsweredSectionCount ?? '-'}
                </p>
            </div>

            <div className="bg-slate-50 rounded-2xl p-4 border border-slate-100 col-span-2">
                <p className="text-xs uppercase tracking-wide text-slate-500">
                Skipped Sections
                </p>
                <p className="text-2xl font-black text-slate-900 mt-1">
                {diagnosticStats.SkippedSectionCount ?? '-'}
                </p>
            </div>

            </div>
        </div>
        <div className="bg-white rounded-3xl border border-slate-100 shadow-sm p-6 hover:border-emerald-200 transition-all h-full">
  <div className="flex items-start justify-between mb-5">
    <div className="p-3 rounded-2xl bg-emerald-50 border border-emerald-100 text-emerald-600">
      <Sparkles size={24} />
    </div>

    <span className="text-3xl font-black tracking-tighter text-emerald-600">
      {displayedStrengths.length}
    </span>
  </div>

  <h4 className="text-sm font-bold text-slate-800 uppercase tracking-[0.15em] mb-4">
    Strength Areas
  </h4>

  {displayedStrengths.length > 0 ? (
    <div className="space-y-3">
      {displayedStrengths.map((item) => (
        <div
          key={item}
          className="flex items-center gap-3 p-4 bg-emerald-50 rounded-2xl border border-emerald-100"
        >
          <Target
            size={16}
            className="text-emerald-600 flex-shrink-0"
          />

          <span className="text-sm font-medium text-emerald-700">
            {item}
          </span>
        </div>
      ))}
    </div>
  ) : (
    <div className="bg-slate-50 rounded-2xl p-4 border border-slate-100">
      <p className="text-sm text-slate-500">
        Strengths will appear here when the backend returns them.
      </p>
    </div>
  )}
</div>
      </div>

      <div className="bg-slate-900 rounded-[2.5rem] p-10 text-white shadow-2xl text-center">
        <h3 className="text-3xl font-bold mb-4 tracking-tight text-white">Ready to Strengthen Talent Acquisition?</h3>
        <p className="text-slate-300 text-lg leading-relaxed max-w-2xl mx-auto mb-8">
          Turn these insights into an actionable plan for hiring, retention, and operational growth.
        </p>
        <button onClick={() => handleBookingClick()} className="px-10 py-4 bg-white text-violet-700 font-bold rounded-full hover:shadow-xl hover:scale-105 transition-all active:scale-95">
          Schedule a Talent Strategy Call
        </button>
      </div>

      <div className="bg-white rounded-3xl border border-slate-200 p-8 shadow-sm">
        <h3 className="text-2xl font-bold text-slate-900 mb-6 flex items-center gap-3">
          <Activity size={22} className="text-violet-600" />
          Results
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {sectionResults.map((section, index) => (
            <div key={`${section.name || 'section'}-${index}`} className="rounded-2xl border border-slate-200 p-5 bg-slate-50/70">
              <div className="flex items-start justify-between gap-3">
                <div className="text-left">
                    <h4 className="font-semibold text-slate-900">
                        {section.name || `Section ${index + 1}`}
                    </h4>

                    <p className="text-xs uppercase tracking-[0.2em] text-slate-400 mt-1">
                        Weighting: {section.weighting ?? 'n/a'}
                    </p>

                    <div className="mt-2 text-xs text-slate-500 space-y-1">
                        <p>
                        Answered: {section.answeredQuestionCount ?? 0}
                        </p>

                        <p>
                        Skipped: {section.skippedQuestionCount ?? 0}
                        </p>
                    </div>
                </div>
                <div className="text-right">
                  <p
                    className={`text-2xl font-bold ${
                        section.isSkipped
                        ? 'text-slate-500'
                        : (section.score ?? 0) >= 70
                        ? 'text-emerald-600'
                        : 'text-amber-600'
                    }`}
                    >{section.isSkipped ? 'Skipped' : `${(section.score ?? 0).toFixed(2)}%`}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-emerald-50/50 rounded-3xl border border-emerald-100 p-8 shadow-sm">
          <h4 className="text-xl font-bold text-slate-900 mb-6 flex items-center gap-3">
            <div className="p-2 bg-emerald-600 rounded-lg text-white">
              <Sparkles size={20} />
            </div>
            Strength Areas
          </h4>
          <ul className="space-y-3">
            {displayedStrengths.length > 0 ? displayedStrengths.map((item) => (
              <li key={item} className="flex items-start gap-3 p-4 bg-white rounded-2xl border border-emerald-50">
                <Target size={18} className="text-emerald-600 mt-0.5 flex-shrink-0" />
                <span className="text-slate-700 text-sm leading-relaxed">{item}</span>
              </li>
            )) : <li className="text-slate-500 text-sm">Strengths will appear here when the backend returns them.</li>}
          </ul>
        </div>
        {displayedImprovements.length > 0 && (
            <div className="bg-amber-50/50 rounded-3xl border border-amber-100 p-8 shadow-sm">
            <h4 className="text-xl font-bold text-slate-900 mb-6 flex items-center gap-3">
                <div className="p-2 bg-amber-600 rounded-lg text-white">
                <Shield size={20} />
                </div>
                Improvement Opportunities
            </h4>
            <ul className="space-y-3">
                {displayedImprovements.map((item) => (
                    <li key={item} className="flex items-start gap-3 p-4 bg-white rounded-2xl border border-amber-50">
                        <Target size={18} className="text-amber-600 mt-0.5 flex-shrink-0"/>
                        <span className="text-slate-700 text-sm leading-relaxed">
                            {item}
                        </span>
                    </li>
                ))}
            </ul>
            </div>
        )}
      </div> */}

      <div className="bg-white rounded-3xl border border-slate-200 p-8 shadow-sm">
        <div className="flex items-center justify-between mb-8">
            <h3 className="text-2xl font-bold text-slate-900 flex items-center gap-3">
            <Lightbulb size={22} className="text-violet-600" />
            Strategic Recommendation
            </h3>
        </div>

        {normalizedRecommendations.length > 0 ? (
            (() => {
            const recommendation = normalizedRecommendations[0];

            return (
                <div className="space-y-6 text-left">

                <div className="flex items-start justify-between gap-4">
                    <div>
                    <h4 className="text-xl font-bold text-slate-900">
                        {recommendation.title}
                    </h4>

                    <p className="text-sm text-slate-500 mt-1">
                        Recommended next steps based on your assessment results
                    </p>
                    </div>

                    <span className="px-3 py-2 rounded-full bg-violet-100 text-violet-700 text-sm font-semibold">
                    Action Plan
                    </span>
                </div>

                {recommendation.overview && (
                    <div className="bg-slate-50 border border-slate-100 rounded-2xl p-5">
                    <p className="text-sm font-semibold uppercase tracking-[0.15em] text-slate-500 mb-3">
                        Overview
                    </p>

                    <p className="text-slate-700 leading-relaxed">
                        {recommendation.overview}
                    </p>
                    </div>
                )}

                {recommendation.recommendedActions?.length ? (
                    <div>
                    <p className="text-sm font-semibold uppercase tracking-[0.15em] text-violet-600 mb-4">
                        Recommended Actions
                    </p>

                    <div className="grid gap-3">
                        {recommendation.recommendedActions.map((action) => (
                        <div
                            key={action}
                            className="
                            flex
                            items-start
                            gap-3
                            p-4
                            rounded-2xl
                            bg-violet-50
                            border
                            border-violet-100
                            "
                        >
                            <Target
                            size={16}
                            className="text-violet-600 mt-1 flex-shrink-0"
                            />

                            <span className="text-sm text-slate-700">
                            {action}
                            </span>
                        </div>
                        ))}
                    </div>
                    </div>
                ) : null}

                {recommendation.expectedOutcome && (
                    <div className="bg-emerald-50 border border-emerald-100 rounded-2xl p-5">
                    <p className="text-sm font-semibold uppercase tracking-[0.15em] text-emerald-700 mb-3">
                        Expected Outcome
                    </p>

                    <p className="text-emerald-900 leading-relaxed">
                        {recommendation.expectedOutcome}
                    </p>
                    </div>
                )}

                </div>
            );
            })()
        ) : (
          <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-8 text-center">
            <p className="font-semibold text-emerald-900">
              No strategic recommendations are required at this time.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
